import { Base } from '@studiometa/js-toolkit';
import { sortItemsByTypeThenDistance } from './StoreDistance.js';
import { buildStoreCardMarkup } from './StoreCardMarkup.js';
import { initStoreOpeningBlock } from './StoreOpeningStatus.js';
import { saveStoreLocatorReference } from './StoreReferencePoint.js';
import { applyStoreFilters, emptyStoreFilters, isStoreFiltersEmpty } from './StoreFilterLogic.js';

/**
 * Liste de résultats de « Nos revendeurs » (chantier store-locator-revendeurs,
 * point 4). Composant volontairement autonome, monté directement sur `#list`
 * (top-level dans `App.config.components`, jamais un enfant de `MapboxMap`) :
 * il ne dépend PAS du chargement de la carte pour s'afficher (invariant R9).
 *
 * Source des données : le JSON déjà rendu par PHP dans l'attribut
 * `data-option-source` de `[data-component="MapboxCluster"]` (voir
 * archive-stores.php) — lu directement comme attribut DOM, disponible dès le
 * rendu de la page, sans attendre que le composant MapboxCluster soit monté
 * (lui reste gated par le chargement de Mapbox). Une seule copie des données
 * dans la page : pas de duplication de l'attribut.
 *
 * Coordination avec la carte, quand elle est disponible :
 * - `MapboxCluster` reste seul responsable de la restriction à la zone
 *   visible (bounds) et du filtre par type ; il diffuse le résultat via
 *   l'évènement `store-locator-items` (voir sa méthode `broadcastItems()`).
 * - Le geocoder (`#geocoder`, dans MapboxCluster) diffuse le point de
 *   recherche via `store-locator-reference`.
 * Si la carte ne se charge jamais (service indisponible), aucun de ces deux
 * évènements n'arrive : la liste reste sur son état initial — tous les
 * magasins de la page, regroupés par type (Concept Store, Corner Store, puis
 * le reste) et triés par distance au centre initial dans chaque groupe, sans
 * distance affichée (R9, scénario « sans carte »).
 */
export default class StoreResultsList extends Base {
  static config = {
    name: 'StoreResultsList',
    options: {
      // Centre initial de la carte (mêmes coordonnées que data-option-center
      // sur #map dans archive-stores.php), utilisé tant qu'aucun point de
      // recherche ni aucune donnée de carte (bounds/centre courant) n'est
      // disponible (R9, critère 5, scénario 12a).
      center: { type: Array, default: () => [2.287592, 48.862725] },
    },
  };

  /** Tous les magasins de la page (avec coordonnées valides — filtrés côté serveur, critère 12). */
  allItems = [];

  /** Magasins actuellement à afficher : `allItems` tant qu'aucune donnée de carte n'est arrivée, sinon ceux diffusés par MapboxCluster (zone visible ∩ filtre type). */
  items = [];

  /** Centre courant de la carte (après un moveend), ou `null` si la carte n'a encore rien diffusé. */
  currentCenter = null;

  /** Point de recherche courant ({lng, lat}), ou `null` (critère 5, scénario 12a). */
  referencePoint = null;

  /**
   * Filtres visibles actifs (chantier store-locator-revendeurs, point 6,
   * critère 3), diffusés par `StoreFilterBar` via l'évènement
   * `store-locator-filters`. Appliqués ici indépendamment de la carte
   * (invariant R9 : la liste et les filtres fonctionnent sans attendre son
   * chargement) — `MapboxCluster` applique le même filtre pour ses
   * marqueurs, une fois chargée.
   */
  activeFilters = emptyStoreFilters();

  /** Colonne de gauche (`.list-pane`), qui défile seule à partir de 861 px. */
  listPane = null;

  /** Ordre des magasins du dernier affichage : sert à remettre la liste en haut quand elle change. */
  lastSignature = '';

  mounted() {
    this.listPane = this.$el.closest('.list-pane');
    this.bindWheelChaining();
    this.bindNativeKeyboard();
    this.allItems = this.readSourceItems();
    this.items = this.allItems;
    this.render();
  }

  destroyed() {
    this.listPane?.removeEventListener('wheel', this.wheelChaining);
    this.unbindNativeKeyboard();
  }

  /**
   * Liste défilante de « Nos revendeurs » (chantier
   * store-locator-molette-recherche, 08/10, critères 7 et 8). À partir de
   * 861 px, `.list-pane` défile seule (voir `archive-stores.css`). Or l'option
   * Divi « défilement fluide » (`Divi/js/smoothscroll.js`, active sur le site)
   * écoute TOUTES les molettes de la fenêtre, appelle `preventDefault()` et
   * fait défiler elle-même l'élément défilable le plus proche — la liste —
   * sans jamais passer la main à la page quand la liste est en butée : c'est
   * ce qui « piégeait » la molette le 28/09. Cet écouteur (sur la
   * colonne, donc avant la fenêtre) arrête la molette à la colonne : le
   * navigateur fait alors le défilement natif, qui enchaîne sur la page en
   * butée haute et basse (aucun `overscroll-behavior` ; `preventDefault()` seulement dans
   * la phase « page d'abord » décrite plus bas).
   *
   * Elle est arrêtée TOUJOURS (≥ 861 px, sans Ctrl/⌘), que la liste ait de quoi
   * défiler ou non : le script de Divi garde pendant 10 s en mémoire l'élément
   * défilant trouvé pour chaque cible ; une liste longue devenue courte ou vide
   * (carte déplacée, filtre) y restait « la liste » et captait encore la
   * molette sans pouvoir défiler. Une liste qui ne défile pas laisse le
   * navigateur défiler la page (enchaînement natif). Ctrl/⌘ + molette et mobile
   * (< 861 px, la liste n'est pas défilante) : inchangés.
   *
   * La page d'abord, puis la liste (revue finale R1, décision de Robin du
   * 08/10) : tant que le bloc liste + carte (`.main`) dépasse le bas de la
   * fenêtre, la molette vers le bas fait défiler la PAGE (jusqu'à ce que le bas
   * du bloc arrive au bas de la fenêtre) ; tant que le haut du bloc est remonté
   * sous l'en-tête (`--header-height`), la molette vers le haut fait remonter
   * la PAGE jusqu'à réaligner le bloc. Le reste du mouvement (et tous les
   * suivants) revient à la liste, puis, en butée, à la page par l'enchaînement
   * natif. Seule cette phase annule la molette (`preventDefault()`) : la page
   * avance du pas exact de la molette (pixels, `deltaMode` converti), sans
   * animation ni saut, et ce qui reste du pas passe à la liste (perdu si la liste
   * est vide ou trop courte pour défiler : au plus un pas de molette). Une fois le
   * bloc aligné, la molette est de nouveau entièrement native (pavé tactile,
   * inertie comprise).
   */
  bindWheelChaining() {
    if (!this.listPane) return;
    this.wheelChaining = (event) => {
      if (event.ctrlKey || event.metaKey) return;
      if (!window.matchMedia('(min-width: 861px)').matches) return;
      event.stopPropagation();
      if (!event.deltaY) return;

      // Pas vertical en pixels (lignes ×16, pages ×hauteur de fenêtre).
      let unit = 1;
      if (event.deltaMode === 1) unit = 16;
      else if (event.deltaMode === 2) unit = window.innerHeight;
      const delta = event.deltaY * unit;

      const block = this.listPane.closest('.main') || this.listPane;
      const rect = block.getBoundingClientRect();
      // Le débord est BORNÉ à 0 : un débord négatif (bloc déjà aligné ou au-delà)
      // changerait le signe du déplacement et ferait partir la page à l'envers ;
      // sans débord, la molette revient au navigateur (liste, puis enchaînement
      // natif sur la page en butée).
      let wanted = 0;
      if (delta > 0) {
        // Bas du bloc sous le bas de la fenêtre : la page descend d'abord.
        wanted = Math.min(delta, Math.max(0, rect.bottom - window.innerHeight));
      } else {
        // Haut du bloc remonté sous l'en-tête : la page remonte d'abord.
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        wanted = -Math.min(-delta, Math.max(0, header - rect.top));
      }
      if (Math.abs(wanted) < 1) return;

      event.preventDefault();
      const before = window.scrollY;
      window.scrollBy({ top: wanted, left: 0, behavior: 'instant' });
      // Ce que la page a réellement parcouru (elle peut être en butée) ; le reste va à la liste
      // (perdu si la liste ne peut pas défiler : au plus un pas de molette).
      const rest = delta - (window.scrollY - before);
      if (Math.abs(rest) >= 1) this.listPane.scrollTop += rest;
    };
    this.listPane.addEventListener('wheel', this.wheelChaining, { passive: false });
  }

  /**
   * Clavier natif sur « Nos revendeurs », à partir de 861 px (revue finale du
   * chantier store-locator-molette-recherche, R2). Le défilement fluide de Divi
   * (`smoothscroll.js`) écoute aussi le clavier (flèches, Page précédente /
   * suivante, Début, Fin, Espace), fait défiler l'élément défilable le plus
   * proche du DERNIER ÉLÉMENT CLIQUÉ et appelle `preventDefault()` : après un
   * clic dans la liste, les touches visaient toujours la liste et ne rendaient
   * jamais la main à la page (Début appliquait même le défilement de la page
   * à la liste). On retire donc son écouteur clavier via l'API publique du
   * script (`window.ET_SmoothScroll.toggleKeydown(false)`) : le navigateur fait
   * le défilement natif, qui enchaîne sur la page en butée. Rétabli sous 861 px
   * (mise en page mobile, liste non défilante) et à la destruction du composant.
   *
   * Le script de Divi (re)pose son écouteur clavier à son initialisation, à la
   * première molette et au chargement complet de la page, et `ET_SmoothScroll`
   * n'existe qu'une fois le script chargé : le retrait est donc repris juste
   * après le chargement de la page et à chaque molette (retirer un écouteur déjà
   * retiré est sans effet). Si le défilement fluide est désactivé dans Divi, le
   * script n'est pas chargé et rien n'est à faire.
   *
   * Divi ne pose son écouteur clavier que sous Chrome (le script teste le
   * navigateur) ET si `<body>` porte la classe `et_smooth_scroll` : ailleurs
   * (Firefox, Safari, classe absente), `ET_SmoothScroll` existe mais son
   * gestionnaire n'a jamais été posé, et `toggleKeydown(true)` l'installerait
   * pour lever une erreur à chaque touche. On n'y touche donc pas (revue finale,
   * recontrôle R3) ; et le rétablissement attend le chargement complet de la
   * page (le gestionnaire de Divi y a alors son état initial).
   */
  bindNativeKeyboard() {
    this.keyboardRemoved = false;
    this.keyboardSyncPending = false;
    this.keyboardQuery = window.matchMedia('(min-width: 861px)');
    this.syncNativeKeyboard = () => {
      const api = window.ET_SmoothScroll;
      if (!api || typeof api.toggleKeydown !== 'function') return;
      // Mêmes conditions que le script de Divi pour poser son écouteur clavier.
      const diviActif = /chrome/i.test(window.navigator.userAgent)
        && document.body.classList.contains('et_smooth_scroll');
      if (!diviActif) return;
      if (this.keyboardQuery.matches) {
        api.toggleKeydown(false);
        this.keyboardRemoved = true;
      } else if (this.keyboardRemoved && document.readyState === 'complete') {
        api.toggleKeydown(true);
        this.keyboardRemoved = false;
      }
    };
    // Reprise après les écouteurs de Divi (load, première molette) : tâche suivante.
    this.scheduleNativeKeyboardSync = () => {
      if (this.keyboardSyncPending) return;
      this.keyboardSyncPending = true;
      window.setTimeout(() => {
        this.keyboardSyncPending = false;
        this.syncNativeKeyboard();
      }, 0);
    };
    this.keyboardQuery.addEventListener('change', this.syncNativeKeyboard);
    window.addEventListener('load', this.scheduleNativeKeyboardSync);
    window.addEventListener('wheel', this.scheduleNativeKeyboardSync, { capture: true, passive: true });
    this.syncNativeKeyboard();
  }

  /** Remet le clavier de Divi comme avant (destruction du composant). */
  unbindNativeKeyboard() {
    if (!this.keyboardQuery) return;
    this.keyboardQuery.removeEventListener('change', this.syncNativeKeyboard);
    window.removeEventListener('load', this.scheduleNativeKeyboardSync);
    window.removeEventListener('wheel', this.scheduleNativeKeyboardSync, { capture: true });
    if (this.keyboardRemoved && document.readyState === 'complete' && window.ET_SmoothScroll?.toggleKeydown) {
      window.ET_SmoothScroll.toggleKeydown(true);
    }
    this.keyboardRemoved = false;
  }

  /**
   * Lit les magasins depuis l'attribut `data-option-source` déjà rendu par
   * PHP sur l'élément MapboxCluster (présent dans le DOM dès le chargement de
   * la page, que le composant MapboxCluster soit monté ou non).
   * @returns {Array}
   */
  readSourceItems() {
    const sourceEl = document.querySelector('[data-component="MapboxCluster"]');
    if (!sourceEl || !sourceEl.dataset.optionSource) {
      return [];
    }
    try {
      const items = JSON.parse(sourceEl.dataset.optionSource);
      return Array.isArray(items) ? items : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Reçoit les magasins visibles diffusés par MapboxCluster (zone visible de
   * la carte ∩ filtre type), à l'initialisation puis à chaque déplacement,
   * zoom ou changement de filtre.
   * @param  {CustomEvent} event
   */
  onWindowStoreLocatorItems({ detail }) {
    this.items = Array.isArray(detail?.items) ? detail.items : this.allItems;
    this.currentCenter = Array.isArray(detail?.center) ? detail.center : null;
    this.render();
  }

  /**
   * Reçoit le point de recherche (résultat du geocoder), ou `null` si la
   * recherche est effacée. Mémorisé pour l'onglet (R6), utilisé pour trier et
   * afficher la distance tant qu'une nouvelle recherche n'est pas faite.
   * @param  {CustomEvent} event
   */
  onWindowStoreLocatorReference({ detail }) {
    this.referencePoint = detail || null;
    saveStoreLocatorReference(this.referencePoint);
    this.render();
  }

  /**
   * Reçoit les filtres actifs diffusés par `StoreFilterBar` (barre de puces,
   * point 6, critère 3), appliqués immédiatement, indépendamment de l'état de
   * la carte (invariant R9).
   * @param  {CustomEvent} event
   */
  onWindowStoreLocatorFilters({ detail }) {
    this.activeFilters = detail || emptyStoreFilters();
    this.render();
  }

  /**
   * Rafraîchissement minute du statut d'ouverture (F5, revue finale du
   * chantier store-locator-revendeurs, 2026-09-24), diffusé par
   * `StoreOpeningStatus.js` (`refreshTrackedStatuses()`, critère 9) : sans
   * ce ré-affichage, un magasin qui passe « Fermé » à la minute pile restait
   * visible tant que le filtre « Ouvert maintenant » actif n'était pas
   * réappliqué (le texte du statut seul se mettait à jour, pas la liste).
   * Aucun effet si ce filtre n'est pas actif, pour ne jamais re-rendre la
   * liste inutilement chaque minute.
   */
  onWindowStoreLocatorMinuteTick() {
    if (this.activeFilters.openNow) {
      this.render();
    }
  }

  /**
   * Point de calcul de distance actif : le point de recherche s'il existe,
   * sinon le centre courant de la carte (une fois connu), sinon le centre
   * initial déclaré (avant tout moveend, ou si la carte ne charge jamais —
   * R9).
   * @returns {{lng: number, lat: number}}
   */
  get distanceOrigin() {
    if (this.referencePoint) {
      return this.referencePoint;
    }
    if (this.currentCenter) {
      return { lng: this.currentCenter[0], lat: this.currentCenter[1] };
    }
    return { lng: this.$options.center[0], lat: this.$options.center[1] };
  }

  /**
   * Message de l'état vide (aucun magasin dans la zone visible).
   * @param  {boolean} hasActiveFilter Au moins un filtre visible est actif.
   * @returns {string}
   */
  buildEmptyStateMarkup(hasActiveFilter) {
    if (hasActiveFilter) {
      return `
        <div class="sl-empty">
          <p class="sl-empty__title">Aucun revendeur ne correspond</p>
          <p class="sl-empty__text">Essayez d'élargir vos filtres ou de rechercher une autre adresse.</p>
          <button type="button" class="sl-empty__reset" data-ref="reset-filters">Réinitialiser les filtres</button>
        </div>
      `;
    }
    return `
      <div class="sl-empty">
        <p class="sl-empty__title">Aucun revendeur ne correspond</p>
        <p class="sl-empty__text">Déplacez la carte ou recherchez une autre adresse.</p>
      </div>
    `;
  }

  render() {
    const showDistance = Boolean(this.referencePoint);
    // Filtres visibles (point 6, critère 3) appliqués avant le tri, sur les
    // magasins déjà restreints à la zone visible de la carte (ou tous les
    // magasins de la page tant qu'aucune carte n'a rien diffusé, R9).
    const filtered = applyStoreFilters(this.items, this.activeFilters);
    // Ordre (chantier store-locator-ordre-types) : Concept Store, puis Corner
    // Store, puis revendeurs et magasins sans catégorie ; dans chaque groupe,
    // distance croissante au point de recherche ou au centre de la carte.
    const sorted = sortItemsByTypeThenDistance(filtered, this.distanceOrigin);

    // Liste défilante (08/10) : quand les magasins affichés ou leur ordre
    // changent (carte déplacée ou zoomée, recherche, filtre), la liste repart
    // du haut — le défilement précédent n'a plus de sens pour une autre liste.
    // Un simple ré-affichage à l'identique (minute, état « Ouvert
    // maintenant ») garde la position.
    const signature = sorted.map((item) => item.url || item.name || '').join('|');
    const listChanged = signature !== this.lastSignature;
    this.lastSignature = signature;

    // F9 (revue finale du chantier store-locator-revendeurs, 2026-09-25) :
    // `this.$el.innerHTML = …` ci-dessous reconstruit chaque carte depuis
    // zéro, perdant l'état déplié d'un éventuel « Voir horaires » ouvert par
    // le visiteur — un rafraîchissement automatique (filtre, minute) le
    // refermait donc sans que le visiteur y touche. Mémorisé par URL de
    // magasin (stable d'un rendu à l'autre, contrairement à l'index), puis
    // rouvert après reconstruction (voir la boucle plus bas).
    const expandedStoreUrls = new Set();
    this.$el.querySelectorAll('.sl-result-card').forEach((card) => {
      const panel = card.querySelector('.store-opening__panel');
      const url = card.querySelector('.list-item[data-url]')?.dataset.url;
      if (panel && !panel.hasAttribute('hidden') && url) {
        expandedStoreUrls.add(url);
      }
    });

    this.$el.innerHTML = sorted.length
      ? sorted
        .map((item, index) => buildStoreCardMarkup(item, {
          distanceKm: showDistance ? item.__distanceKm : null,
          index,
        }))
        .join('')
      // État vide (maquette de Robin, scénarios 7/21) : le bouton de
      // réinitialisation n'est proposé que si au moins un filtre est actif
      // (même état que la liste : filtres diffusés par `StoreFilterBar`) ;
      // sans filtre, le visiteur est invité à déplacer la carte ou à
      // chercher une autre adresse (chantier store-locator-bouton-plein-ecran).
      : this.buildEmptyStateMarkup(!isStoreFiltersEmpty(this.activeFilters));

    if (listChanged && this.listPane) {
      this.listPane.scrollTop = 0;
    }

    // Les deux `.list-item` de chaque carte (nom, « En savoir plus ») sont
    // des liens natifs (`href`) : aucun câblage JS de navigation nécessaire
    // (revue d'implémentation — un `<a>` natif permet ouverture dans un
    // nouvel onglet, clic milieu, etc., contrairement à un `<button>` + JS).

    sorted.forEach(({ opening_hours_week: week, url }, index) => {
      const openingEl = this.$el.querySelector(`.store-opening[data-store-index="${index}"]`);
      if (openingEl) {
        const normalizedWeek = week && Object.keys(week).length > 0 ? week : null;
        initStoreOpeningBlock(openingEl, normalizedWeek);
        // F9 : rouvre « Voir horaires » si ce magasin l'était avant ce rendu
        // (clic normal sur le bouton déjà câblé par initStoreOpeningBlock
        // ci-dessus, jamais une écriture propre à ce mécanisme).
        if (expandedStoreUrls.has(url)) {
          openingEl.querySelector('[data-ref="toggle"]')?.click();
        }
      }
    });

    // « Réinitialiser les filtres » (état vide, point 6) : rendu par ce
    // composant, mais la barre de puces qui possède l'état coché vit ailleurs
    // (`StoreFilterBar`, hors de `#list`) — coordination par un évènement
    // global, sur le même principe que `store-locator-filters`.
    const resetButton = this.$el.querySelector('[data-ref="reset-filters"]');
    resetButton?.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('store-locator-filters-reset'));
    });
  }
}
